import { useState } from 'react'
import { X, Plus, Trash2, AlertTriangle } from 'lucide-react'
import { lopsidedProducts } from '@petrotrack/shared'
import { FUEL_COLORS } from '../../utils/formatters'

const FUEL_TYPES = ['PMS', 'AGO', 'DPK', 'LPG']

function buildForm(branch, organizations) {
  if (!branch) return { name: '', location: '', organizationId: organizations.length > 0 ? organizations[0].id : '' }
  return { name: branch.name, location: branch.location, organizationId: branch.organizationId }
}
const buildTanks = (branch) => (branch?.tanks || []).map((t) => ({ id: t.id, product: t.product, tankNumber: t.tankNumber, capacity: t.capacity }))
const buildPumps = (branch) => (branch?.pumps || []).map((p) => ({ id: p.id, product: p.product, pumpNumber: p.pumpNumber, name: p.name || '', defaultExcessPer20L: p.defaultExcessPer20L ?? 1.0 }))

const cell = 'w-full px-2 py-1.5 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none'

export default function BranchModal({ branch, organizations, onSave, onClose }) {
  const [form, setForm] = useState(() => buildForm(branch, organizations))
  const [tanks, setTanks] = useState(() => buildTanks(branch))
  const [pumps, setPumps] = useState(() => buildPumps(branch))
  const [error, setError] = useState('')
  const isEditing = !!branch

  const setField = (field, value) => setForm((p) => ({ ...p, [field]: value }))
  const updTank = (i, field, value) => setTanks((prev) => prev.map((t, idx) => idx === i ? { ...t, [field]: value } : t))
  const updPump = (i, field, value) => setPumps((prev) => prev.map((p, idx) => idx === i ? { ...p, [field]: value } : p))

  // Hard block: a product configured one-sided (tank without pump, or pump
  // without tank) makes daily entries impossible and the entry form look
  // broken — refuse to save until every product has both.
  // Only meaningful while creating — that is the only path where this modal
  // still writes tanks and pumps.
  const lopsided = isEditing ? [] : lopsidedProducts({ tanks, pumps })

  function handleSubmit() {
    if (!form.name.trim()) return setError('Branch name is required.')
    if (!form.organizationId) return setError('Please select an organization.')
    for (const [i, t] of tanks.entries()) if (!t.capacity || Number(t.capacity) <= 0) return setError(`Tank ${i + 1}: capacity must be > 0.`)
    if (lopsided.length > 0) {
      return setError(
        lopsided.map((r) => `${r.product} needs ${r.missing.join(' and ')}`).join('; ') +
        '. Every product must have at least one tank AND one pump before the branch can be saved.'
      )
    }
    setError('')
    if (isEditing) return onSave({ ...form })
    onSave({
      ...form,
      tanks: tanks.map((t) => ({ id: t.id, product: t.product, tankNumber: Number(t.tankNumber), capacity: Number(t.capacity) })),
      pumps: pumps.map((p) => ({ id: p.id, product: p.product, pumpNumber: Number(p.pumpNumber) || undefined, name: p.name, defaultExcessPer20L: Math.max(0, Number(p.defaultExcessPer20L) || 0) })),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
          <div><h2 className="text-base sm:text-lg font-bold text-gray-800">{isEditing ? 'Edit Branch' : 'Add Branch'}</h2>{isEditing && <p className="text-xs text-gray-500 mt-0.5">{branch.name}</p>}</div>
          <button onClick={onClose} className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">
          {/* Branch details */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 pb-1 border-b border-gray-100">Branch Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Branch Name *</label><input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="e.g. Ikeja Depot" /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Location</label><input className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500" value={form.location} onChange={(e) => setField('location', e.target.value)} placeholder="e.g. Lagos" /></div>
              <div className="sm:col-span-2"><label className="block text-xs font-medium text-gray-600 mb-1">Organization *</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none bg-white focus:ring-2 focus:ring-blue-500" value={form.organizationId} onChange={(e) => setField('organizationId', e.target.value)}>
                  <option value="">Select organization...</option>{organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {isEditing && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-700">Tanks &amp; pumps</h3>
              <p className="text-xs text-gray-500 mt-1">
                Managed in <span className="font-semibold">Station Calibration</span>, where each tank and
                pump is added or retired on its own. Editing them here used to rewrite the whole station,
                which discarded its meter and dip readings.
              </p>
            </div>
          )}

          {!isEditing && (
          <>
          {/* Tanks */}
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-1 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Tanks <span className="text-xs font-normal text-gray-400">({tanks.length})</span></h3>
              <button onClick={() => setTanks([...tanks, { product: 'PMS', tankNumber: tanks.length + 1, capacity: 50000 }])} className="flex items-center space-x-1 text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded-lg"><Plus className="w-3 h-3" /><span>Add Tank</span></button>
            </div>
            <div className="space-y-2">
              {tanks.map((tank, i) => {
                const c = FUEL_COLORS[tank.product] || FUEL_COLORS.PMS
                return (
                  <div key={tank.id || i} className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{tank.product}</span>
                    <select className={cell} value={tank.product} onChange={(e) => updTank(i, 'product', e.target.value)}>{FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}</select>
                    <input className={cell} type="number" value={tank.tankNumber} onChange={(e) => updTank(i, 'tankNumber', e.target.value)} placeholder="Tank #" />
                    <input className={cell} type="number" value={tank.capacity} onChange={(e) => updTank(i, 'capacity', e.target.value)} placeholder="Capacity" />
                    <button onClick={() => setTanks(tanks.filter((_, idx) => idx !== i))} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Pumps (branch + product) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-1 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">Pumps <span className="text-xs font-normal text-gray-400">({pumps.length})</span></h3>
              <button onClick={() => setPumps([...pumps, { product: 'PMS', pumpNumber: pumps.length + 1, name: '', defaultExcessPer20L: 1.0 }])} className="flex items-center space-x-1 text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded-lg"><Plus className="w-3 h-3" /><span>Add Pump</span></button>
            </div>
            <p className="text-xs text-gray-400">Pumps draw from any tank of their product.</p>
            <div className="space-y-2">
              {pumps.map((pump, i) => {
                const c = FUEL_COLORS[pump.product] || FUEL_COLORS.PMS
                return (
                  <div key={pump.id || i} className="flex items-center gap-2 border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.bg} ${c.text}`}>{pump.product}</span>
                    <select className={cell} value={pump.product} onChange={(e) => updPump(i, 'product', e.target.value)}>{FUEL_TYPES.map((f) => <option key={f} value={f}>{f}</option>)}</select>
                    <input className={cell} type="number" value={pump.pumpNumber} onChange={(e) => updPump(i, 'pumpNumber', e.target.value)} placeholder="Pump #" />
                    <input className={cell} value={pump.name} onChange={(e) => updPump(i, 'name', e.target.value)} placeholder="Name (optional)" />
                    <input className={cell} type="number" step="0.1" value={pump.defaultExcessPer20L} onChange={(e) => updPump(i, 'defaultExcessPer20L', e.target.value)} placeholder="/20L" />
                    <button onClick={() => setPumps(pumps.filter((_, idx) => idx !== i))} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )
              })}
            </div>
          </div>
          </>
          )}
        </div>

        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex flex-col gap-3">
          {lopsided.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
              <span>{lopsided.map((r) => `${r.product} is missing ${r.missing.join(' and ')}`).join(' · ')} — daily entries need both.</span>
            </div>
          )}
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>}
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={handleSubmit} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg">{isEditing ? 'Update Branch' : 'Add Branch'}</button>
            <button onClick={onClose} className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2.5 rounded-lg">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  )
}
